import type { WritableDraft } from 'immer';

import { FieldValidationLevel, validatePrecision, type FieldValidationRule, type NumericField } from '@talos/kyoko';
import Big from 'big.js';
import { UNITS_CURRENCY } from '../CareOrderFormView/types';
import type { RFQState } from './types';

export const quantityValidation: FieldValidationRule<NumericField, WritableDraft<RFQState>> = (field, context) => {
  if (!context) {
    return null;
  }

  const { form, referenceData } = context;

  const security = form?.symbolField.value;
  if (!security) {
    return null;
  }

  const quantity = new Big(field.value || 0);
  const orderCurrency = form.rfqCurrencyField.value || '';

  const { MinimumSize, MinSizeIncrement, QuoteCurrency } = security;
  const orderCurrencyInfo = referenceData.currencies.currenciesBySymbol.get(orderCurrency);
  const { MinIncrement } = orderCurrencyInfo || {};

  let minSize = MinimumSize;
  let minIncr = MinSizeIncrement;
  if (orderCurrency === QuoteCurrency && MinIncrement) {
    minSize = MinIncrement;
    minIncr = MinIncrement;
  }

  if (form.rfqCurrencyField.value === UNITS_CURRENCY) {
    /**
     * This defaulting is needed temporarily because the unit-related securities the backend are creating are wrong
     * Todo, check this out again in the future and see if the securities are populated with these two properties.
     */
    minSize = MinimumSize ?? '1';
    minIncr = MinSizeIncrement ?? '1';
  }

  if (quantity.lt(minSize)) {
    return {
      message: `Min quantity is ${minSize}`,
      level: FieldValidationLevel.Error,
    };
  }

  if (!validatePrecision(minIncr, field.value)) {
    return {
      message: `Min increment is ${minIncr}`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

export const careOrderQuantityValidation: FieldValidationRule<NumericField, WritableDraft<RFQState>> = (
  field,
  context
) => {
  const orderQty = field.value;
  if (context?.form.parentIDField.value == null || !context?.form.assignToParentField.value || !orderQty) {
    return null;
  }

  const currentCareOrder = context?.currentCareOrder;

  if (!currentCareOrder) {
    return {
      message: 'Loading care order...',
      level: FieldValidationLevel.Error,
    };
  }

  if (Big(orderQty).gt(currentCareOrder.AvailableQty)) {
    return {
      message: `Quantity cannot exceed available care order quantity: ${currentCareOrder.AvailableQty}`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};
